import React, { useState, memo, useEffect } from 'react';
import * as PropTypes from 'prop-types';
import useUiContext from '@groupefungo/react-apollo.core/ui/UseContext';
import { useAppContext } from '@groupefungo/react-apollo.core/app/UseContext/useAppContext';
import qs from 'query-string';
import useCalendarStyles from '../availabilities/hooks/useCalendarStyles';
import CalendarToolQuery from './CalendarToolQuery';
import CalendarView from './CalendarView';
import BookingCalendarAvailabilities from './BookingCalendarAvailabilities';

function Calendar(props) {
  const {
    children,
    calendarType,
    setCalendarType,
    queryParams,
    displaySideHours,
    displayHourCase,
    handleCalendarCaseClick,
    dateOverride,
    highlightToday,
    admin = true,
    handleSelectHour,
    handleContinue,
    appointmentType = null,
    setSelectedAvailability,
    selectedAvailability,
    processData,
    alterations,
    alterationRange = {},
  } = props;
  const { Button, ArrowBackIcon, ArrowForwardIcon } = useUiContext();
  const { classes } = useCalendarStyles();

  const [date, setDate] = useState(dateOverride || new Date());
  const [navigations, setNavigations] = useState({ previous: '', next: '' });
  const [availabilities, setAvailabilities] = useState([]);
  const [selectedCalendarDay, setSelectedCalendarDay] = useState();

  const { useHistory } = useAppContext();
  const history = useHistory();
  const { previous, next } = navigations;

  useEffect(() => {
    if (dateOverride) setDate(dateOverride);
  }, [dateOverride]);

  useEffect(() => {
    if (!admin) {
      setSelectedAvailability('-1');
    }
  }, [selectedCalendarDay]);

  const handleSliderClick = (newDate) => () => {
    setDate(newDate);
  };

  const getQueryParams = () => qs.parse(history.location.search);

  const onWeekDayClick = (newDate) => () => {
    setCalendarType('Day');
    setDate(newDate);
    const qp = getQueryParams();
    const newQueries = { ...qp, view: 'Day' };
    history.replace({ pathname: history.location.pathname, search: qs.stringify(newQueries) });
  };

  const onCalendarCaseClick = (datetime) => (e) => {
    e.preventDefault();
    if (handleCalendarCaseClick) {
      handleCalendarCaseClick(datetime);
    }
  };

  const RenderCalendarActions = () => (
    <div className="calendar-btn-wrapper">
      <Button
        id="previous_calendar_arrow"
        onClick={handleSliderClick(previous)}
        disabled={!previous}
      >
        <ArrowBackIcon />
      </Button>
      <Button id="next_calendar_arrow" onClick={handleSliderClick(next)} disabled={!next}>
        <ArrowForwardIcon />
      </Button>
    </div>
  );

  return (
    <div className={`${classes.calendarContainer} calendar-container-wrapper ${!admin ? 'clientside-calendar-view' : ''}`}>
      <div className="calendar-restrict-width">
        {admin && <RenderCalendarActions />}
        <div className="calendar-scroll-wrapper">
          <CalendarToolQuery
            queryParams={queryParams}
            setNavigations={setNavigations}
            date={date}
            calendarType={calendarType}
          >
            {(calendar, refetch) => (
              <CalendarView
                calendar={calendar}
                calendarViewType={calendarType}
                onWeekDayClick={onWeekDayClick}
                displaySideHours={displaySideHours}
                displayHourCase={displayHourCase}
                onCalendarCaseClick={onCalendarCaseClick}
                highlightToday={highlightToday}
                admin={admin}
                selectedCalendarDay={selectedCalendarDay}
                setSelectedCalendarDay={setSelectedCalendarDay}
                setDate={setDate}
                navigations={navigations}
                setAvailabilities={setAvailabilities}
                alterations={alterations}
                alterationRange={alterationRange}
                appointmentType={appointmentType}
              >
                {(object) => children(object, calendarType, refetch)}
              </CalendarView>
            )}
          </CalendarToolQuery>
          {selectedCalendarDay && <div className="main-available-timeslots">
            <BookingCalendarAvailabilities
              admin={admin}
              selectedCalendarDay={selectedCalendarDay}
              handleContinue={handleContinue}
              handleSelectHour={handleSelectHour}
              appointmentType={appointmentType}
              selectedAvailability={selectedAvailability}
              processData={processData}
            />
          </div>}
        </div>
      </div>
    </div>
  );
}

Calendar.defaultProps = {
  displaySideHours: true,
  displayHourCase: true,
  calendarType: 'Week',
  setCalendarType: null,
  handleCalendarCaseClick: null,
  dateOverride: null,
  highlightToday: false,
  alterations: false,
  alterationRange: {},
};
Calendar.propTypes = {
  queryParams: PropTypes.object.isRequired,
  displaySideHours: PropTypes.bool,
  displayHourCase: PropTypes.bool,
  children: PropTypes.func.isRequired,
  calendarType: PropTypes.string,
  setCalendarType: PropTypes.func,
  handleCalendarCaseClick: PropTypes.func,
  dateOverride: PropTypes.string,
  highlightToday: PropTypes.bool,
  alterations: PropTypes.bool,
  alterationRange: PropTypes.object,
};

export default memo(Calendar);
